import { useEffect, useMemo, useState } from 'react';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    Banner,
    Button,
    debounce,
    DebounceTimeoutValues,
    DropdownItem,
    Order,
    Stack,
    TableState,
} from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { Option } from '@mews-commander/types';
import {
    DetailedUser,
    PortfolioEnterpriseRelationshipFilters,
    PortfolioEnterpriseRelationshipResult,
} from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { compactArray, uniqById } from '@mews/fp';
import { useAssignProperties } from './useAssignProperties';
import { useGetProperties } from './useGetProperties';

interface AssignPropertiesDialogProps {
    userId: string;
    onClose: () => void;
    active: boolean;
}

const tableOptions: TableState<PortfolioEnterpriseRelationshipResult, PortfolioEnterpriseRelationshipFilters> = {
    filters: {},
    pagination: { pageSize: 100, pageIndex: 0 },
    sort: {
        name: {
            order: Order.Ascending,
            priority: 1,
        },
    },
    globalFilters: {},
    expanded: {},
};

export const AssignPropertiesDialog: React.FC<AssignPropertiesDialogProps> = ({ userId, active, onClose }) => {
    const [search, setSearch] = useState('');

    const { renderFormContent, submit, watch, detailedUser, isLoading } = useAssignProperties({
        userId,
        onSuccess: onClose,
    });

    useEffect(() => {
        setSearch('');
    }, [active]);

    const isSourceEnterpriseIdNeeded = detailedUser?.overrideComparator?.allEqual === false;

    const enterpriseIds = watch('enterpriseIds');
    const sourceEnterpriseId = watch('sourceEnterpriseId');

    const sourceEnterpriseOptions = useSourceEnterpriseOptions({ detailedUser });
    const assignablePropertiesOptions = useAssignablePropertiesOptions({
        detailedUser,
        search,
        enterpriseIds,
    });

    const onSearch = debounce((value: string) => {
        setSearch(value);
    }, DebounceTimeoutValues.textInput);

    const content = (
        <Stack vertical spacing="400">
            <FormSelectField
                name="enterpriseIds"
                label={<LocalizedText textKey={TextKey.UserProfileAssignPropertiesSelectPropertiesInputLabel} />}
                size="medium"
                multiselect
                required
                onSearchChange={onSearch}
                preserveMultiSelectSearch
                helper={<LocalizedText textKey={TextKey.UserProfileAssignPropertiesSelectPropertiesInputCaption} />}
            >
                {assignablePropertiesOptions.map((option) => (
                    <DropdownItem
                        label={option.label}
                        description={option.secondaryText}
                        value={String(option.value)}
                        key={String(option.value)}
                    />
                ))}
            </FormSelectField>
            {isSourceEnterpriseIdNeeded ? (
                <>
                    <FormSelectField
                        name="sourceEnterpriseId"
                        label={
                            <LocalizedText textKey={TextKey.UserProfileAssignPropertiesCopyFromPropertyInputLabel} />
                        }
                        helper={
                            <LocalizedText textKey={TextKey.UserProfileAssignPropertiesCopyFromPropertyInputCaption} />
                        }
                        size="medium"
                    >
                        {sourceEnterpriseOptions.map((option) => (
                            <DropdownItem
                                label={option.label}
                                description={option.secondaryText}
                                value={String(option.value)}
                                key={String(option.value)}
                            />
                        ))}
                    </FormSelectField>
                    <Banner
                        primaryText={<LocalizedText textKey={TextKey.UserManagementSomeDetailsNotDuplicatedTitle} />}
                        secondaryText={<LocalizedText textKey={TextKey.UserManagementSomeDetailsNotDuplicatedText} />}
                        variant="info"
                    />
                </>
            ) : null}
        </Stack>
    );

    return renderFormContent(
        <FloatingPanel
            active={active}
            footer={{
                primary: (
                    <Button
                        type="submit"
                        variant="primary"
                        onClick={submit}
                        disabled={isSourceEnterpriseIdNeeded && !sourceEnterpriseId}
                        size="medium"
                    >
                        <LocalizedText textKey={TextKey.Assign} />
                    </Button>
                ),
                secondary: (
                    <Button type="button" variant="tertiary" onClick={onClose} size="medium">
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
            }}
            onClose={onClose}
            title={
                <LocalizedText
                    textKey={TextKey.AssignPropertiesTitle}
                    substitutes={{ user: detailedUser?.fullName || '' }}
                />
            }
            loading={isLoading}
            size="small"
        >
            {content}
        </FloatingPanel>
    );
};

const useSourceEnterpriseOptions = ({ detailedUser }: { detailedUser?: DetailedUser }) => {
    const { data } = useGetProperties({ ...tableOptions });
    const allProperties = useMemo(() => data?.data || [], [data]);

    const sourceEnterpriseOptions: Option[] = [];

    for (const property of allProperties) {
        const isAlreadyAssignedProperty = detailedUser?.enterprises?.find(({ id }) => id === property.id);
        const option = createOption({
            label: property.name,
            secondaryText: property.cityName,
            value: property.id,
        });

        if (isAlreadyAssignedProperty) {
            sourceEnterpriseOptions.push(option);
        }
    }

    return sourceEnterpriseOptions;
};

const useAssignablePropertiesOptions = ({
    detailedUser,
    search,
    enterpriseIds,
}: {
    detailedUser?: DetailedUser;
    search?: string;
    enterpriseIds: string[];
}) => {
    const [selectedProperties, setSelectedProperties] = useState<PortfolioEnterpriseRelationshipResult[]>([]);

    const { data } = useGetProperties({
        ...tableOptions,
        globalFilters: { search },
    });

    const filteredProperties = useMemo(() => data?.data || [], [data]);

    useEffect(() => {
        // we need to keep selected properties in the dropdown even when they don't match the search
        if (Array.isArray(enterpriseIds)) {
            setSelectedProperties((currentValue) => {
                const allProperties = uniqById([...currentValue, ...filteredProperties]);
                return compactArray(enterpriseIds.map((id) => allProperties.find((p) => p.id === id)));
            });
        }
    }, [enterpriseIds, filteredProperties, setSelectedProperties]);

    const assignablePropertiesOptions = [];
    const filteredAndSelectedProperties = uniqById([...filteredProperties, ...selectedProperties]);

    for (const property of filteredAndSelectedProperties) {
        const isAlreadyAssignedProperty = detailedUser?.enterprises?.find(({ id }) => id === property.id);
        const option = createOption({
            label: property.name,
            secondaryText: property.cityName,
            value: property.id,
        });

        if (!isAlreadyAssignedProperty) {
            assignablePropertiesOptions.push(option);
        }
    }

    return assignablePropertiesOptions;
};
